// HealthSherpaIntegrationView — donde cada agente conecta SU PROPIA cuenta de
// HealthSherpa.
//
// SOLO LA PUERTA DE ENTRADA. De aquí todavía no entra ni una póliza: leer el
// estado de las pólizas necesita que HealthSherpa apruebe el acceso, y hasta
// entonces no se pueden ver sus campos reales. Lo que esta pantalla hace es
// guardar la llave cifrada y decir en qué punto del trámite está el agente.
//
// POR QUE CADA AGENTE LA SUYA: HealthSherpa confirmó que una llave se ata a UNA
// sola cuenta de agente. Una agencia con cinco agentes que venden son cinco
// llaves, cada una en su perfil. Ninguno ve ni toca la del otro.
//
// LOS ASISTENTES NO LLEGAN AQUI, y es a propósito: un asiento es un asiento
// pagado pero no es agente, no tiene cuenta propia en HealthSherpa y no tiene
// llave que pegar. El servidor lo bloquea con requireAgentOrAbove() y la barra
// lateral no se lo enseña (no está en SEAT_NAV_PERMISSION_MAP, que es
// deniega-por-defecto). Sí ve las pólizas ya actualizadas de su agente.
//
// EL ESTADO QUE MAS IMPORTA ES "ESPERANDO APROBACION". HealthSherpa contesta
// el MISMO error cuando la llave fue revocada, cuando fue rotada y cuando
// todavía no la han aprobado. El tercero es el del PRIMER DIA — el más común
// al conectar. Si esta pantalla dijera ahí "vuelve a conectarla", el agente
// pegaría la misma llave una y otra vez hasta concluir que Deskte está roto.
// Por eso el servidor lo separa con un dato nuestro (si la llave acertó alguna
// vez) y aquí se pinta como una espera tranquila, no como un error.
import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Plug, Trash2, ShieldCheck, ExternalLink, Check, AlertTriangle, CheckCircle2, Clock, HeartPulse, RefreshCw, Copy, Eye } from "lucide-react";
import {
  getHealthSherpaConnection,
  connectHealthSherpa,
  testHealthSherpa,
  disconnectHealthSherpa,
  getHealthSherpaApplications,
} from "../api.js";
import { confirmDialog } from "./ui.jsx";
import { toast } from "./Toast.jsx";

const PORTAL_URL = "https://one.healthsherpa.com";
const TERMS_URL = "https://one.healthsherpa.com/terms";

export function HealthSherpaIntegrationView({ lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);

  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({ apiKey: "", accountEmail: "", acceptedTerms: false });
  // "Ni sí ni no": HealthSherpa no contestó. NO es que la conexión se cayera,
  // así que se dice aparte en vez de cambiar el estado de la tarjeta.
  const [sinRespuesta, setSinRespuesta] = useState(false);
  // Lo que se vio al preguntar: la URL, el codigo y lo que contestaron. Es lo
  // UNICO que dice si el rechazo lo puso HealthSherpa o algo nuestro por el
  // camino: un 403 suyo y un 403 de un proxy son el mismo numero.
  const [diag, setDiag] = useState(null);
  const [copiado, setCopiado] = useState(false);
  // EL VISTAZO: lo que HealthSherpa contesta de verdad. No se guarda en ningún
  // sitio — vive en esta pantalla y se va al recargar. Ver el comentario del
  // bloque de abajo.
  const [vistazo, setVistazo] = useState(null);
  const [mirando, setMirando] = useState(false);
  const [copiadoResumen, setCopiadoResumen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setConn(await getHealthSherpaConnection());
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const card = {
    background: "var(--c-card,#0f2433)", border: "1px solid var(--c-border,#1c3a4f)",
    borderRadius: 12, padding: "1.25rem", marginBottom: "1rem",
  };
  const btn = (bg, fg = "#fff") => ({
    display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px",
    background: bg, color: fg, border: "none", borderRadius: 8, fontSize: 14,
    fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
  });
  const inp = {
    width: "100%", padding: "9px 11px", borderRadius: 8, fontSize: 14,
    background: "var(--c-surface2,#0b1c2b)", color: "var(--c-white,#eef6ff)",
    border: "1px solid var(--c-border,#1c3a4f)", boxSizing: "border-box",
  };
  const lbl = { display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 5, color: "var(--c-text2,#7aa3c0)" };
  const help = { fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 5 };

  const state = conn?.state || "not_connected";

  /** Lo que ve el agente en cada estado. El texto importa tanto como el color:
   *  "esperando aprobación" tiene que dejar clarísimo que NO tiene nada que
   *  hacer, o se pone a reconectar en vano. */
  const BADGE = {
    not_connected: {
      color: "var(--c-text2,#7aa3c0)", bg: "transparent", Icon: Plug,
      label: t("Sin conectar", "Not connected"),
    },
    pending_approval: {
      color: "#fbbf24", bg: "rgba(251,191,36,.12)", Icon: Clock,
      label: t("Esperando aprobación", "Waiting for approval"),
    },
    // Entraron y rechazaron NUESTRA petición. No es ámbar de "espera a que
    // te aprueben" ni rojo de "algo tuyo se rompió": el trabajo es de Deskte.
    request_invalid: {
      color: "#7aa3c0", bg: "rgba(122,163,192,.12)", Icon: AlertTriangle,
      label: t("Estamos corrigiéndolo", "We're fixing it"),
    },
    connected: {
      color: "#4ade80", bg: "rgba(74,222,128,.12)", Icon: CheckCircle2,
      label: t("Conectado", "Connected"),
    },
    disconnected: {
      color: "#f87171", bg: "rgba(248,113,113,.12)", Icon: AlertTriangle,
      label: t("Se perdió la conexión", "Connection lost"),
    },
  }[state];

  const handleSave = async () => {
    setError(""); setSinRespuesta(false); setDiag(null);
    if (!form.apiKey.trim()) {
      setError(t("Falta la llave.", "The key is missing."));
      return;
    }
    if (!form.accountEmail.trim()) {
      setError(t("Falta el correo de tu cuenta.", "Your account email is missing."));
      return;
    }
    if (!form.acceptedTerms) {
      setError(t(
        "Tienes que marcar que conoces los términos de desarrollador de HealthSherpa.",
        "You have to confirm you've seen HealthSherpa's developer terms.",
      ));
      return;
    }
    setBusy(true);
    try {
      const data = await connectHealthSherpa({
        apiKey: form.apiKey.trim(),
        accountEmail: form.accountEmail.trim(),
        acceptedTerms: true,
      });
      setConn(data);
      setOpenForm(false);
      setForm({ apiKey: "", accountEmail: "", acceptedTerms: false });
      toast.success(t("Llave guardada.", "Key saved."));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleComprobar = async () => {
    setError(""); setSinRespuesta(false); setBusy(true);
    try {
      const data = await testHealthSherpa();
      setConn(data);
      setDiag(data?.diag || null);
      if (data?.unverified) {
        setSinRespuesta(true);
      } else if (data?.state === "connected") {
        toast.success(t("Conectado.", "Connected."));
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  // Lo que se vio al mirar, cuando sale mal. VA APARTE del `diag` del
  // comprobador y se pinta JUNTO AL BOTON, no arriba en un desplegable
  // cerrado: Lissette pulso "Mirar ahora", le salio un recuadro rojo que
  // ponia "HTTP 400" y nada mas, y el cuerpo —lo unico que dice QUE les
  // falta— estaba escondido en otra tarjeta. Un error que no se puede leer
  // sin que alguien te diga donde buscar no sirve de nada.
  const [diagVistazo, setDiagVistazo] = useState(null);

  const handleVistazo = async () => {
    setError(""); setMirando(true); setVistazo(null); setDiagVistazo(null);
    try {
      // 25 de la lista, y el estado a fondo de las 5 primeras. Cinco y no
      // todas porque cada detalle es una llamada contra la cuota de la agente,
      // y con cinco ya se ve qué campos mandan.
      setVistazo(await getHealthSherpaApplications(25, 5));
    } catch (err) {
      setError(err.message || String(err));
      if (err.diag) setDiagVistazo(err.diag);
    } finally {
      setMirando(false);
    }
  };

  const handleDisconnect = async () => {
    const ok = await confirmDialog({
      message: t(
        "Se borra tu llave de HealthSherpa de Deskte. Tus clientes y pólizas NO se tocan — lo único que pasa es que dejan de actualizarse solos. ¿Continuar?",
        "Your HealthSherpa key will be deleted from Deskte. Your clients and policies are NOT touched — they just stop updating on their own. Continue?",
      ),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await disconnectHealthSherpa();
      setConn({ connected: false, state: "not_connected" });
      toast.success(t("Desconectado.", "Disconnected."));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <HeartPulse size={22} color="#00d4ff" />
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>HealthSherpa</h2>
      </div>
      <p style={{ color: "var(--c-text2,#7aa3c0)", fontSize: 13.5, marginTop: 0, marginBottom: "1.25rem" }}>
        {t(
          "Conecta tu cuenta para que Deskte se entere solo cuando una póliza tuya se cancela o se termina. Se conecta una vez y no hay que volver a tocarlo.",
          "Connect your account so Deskte finds out on its own when one of your policies is cancelled or terminated. You connect once and never touch it again.",
        )}
      </p>

      {error && (
        <div style={{ ...card, background: "#3a1620", borderColor: "#7f1d1d", color: "#fecaca" }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...card, display: "flex", alignItems: "center", gap: 10, color: "var(--c-text2,#7aa3c0)" }}>
          <Loader2 size={16} className="dkt-spin" /> {t("Cargando…", "Loading…")}
        </div>
      ) : (
        <>
          {/* Estado actual */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px",
                  borderRadius: 100, fontSize: 12.5, fontWeight: 700,
                  background: BADGE.bg, color: BADGE.color,
                  border: `1px solid ${BADGE.bg === "transparent" ? "var(--c-border,#1c3a4f)" : BADGE.color}`,
                }}>
                  <BADGE.Icon size={14} /> {BADGE.label}
                </span>
                {conn?.accountEmail && (
                  <div style={{ fontSize: 13, color: "var(--c-text2,#7aa3c0)", marginTop: 10 }}>
                    {conn.accountEmail}
                  </div>
                )}
              </div>
              {conn?.connected && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {/* El boton que faltaba. Sin el, la pantalla no tenia forma
                      de enterarse de nada: decia "Esperando aprobacion" para
                      siempre, incluso con el acceso ya aprobado. */}
                  <button onClick={handleComprobar} disabled={busy} style={btn("transparent", "#00d4ff")}>
                    {busy ? <Loader2 size={15} className="dkt-spin" /> : <RefreshCw size={15} />}
                    {t("Comprobar ahora", "Check now")}
                  </button>
                  <button onClick={handleDisconnect} disabled={busy} style={btn("transparent", "#f87171")}>
                    <Trash2 size={15} /> {t("Desconectar", "Disconnect")}
                  </button>
                </div>
              )}
            </div>

            {/* El mensaje largo de cada estado. Aquí es donde se evita que el
                agente del primer día se ponga a reconectar sin motivo. */}
            {sinRespuesta && (
              <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, background: "rgba(122,163,192,.1)", border: "1px solid var(--c-border,#1c3a4f)", fontSize: 13, lineHeight: 1.55 }}>
                {t(
                  "HealthSherpa no contestó ahora mismo, así que no pudimos comprobarlo. No es que tu conexión se haya caído — tu llave sigue guardada igual. Inténtalo de nuevo en un rato.",
                  "HealthSherpa didn't answer just now, so we couldn't check. It does NOT mean your connection dropped — your key is still saved. Try again in a little while.",
                )}
              </div>
            )}
            {/* EL DIAGNOSTICO. Solo sale cuando algo fallo, y es lo que
                permite saber QUIEN dijo que no: si el texto es de
                HealthSherpa falta la aprobacion; si habla de proxy o de host
                bloqueado, el fallo es nuestro y hay que arreglarlo aqui. */}
            {diag && diag.status !== null && (diag.status < 200 || diag.status >= 300) && (
              <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", userSelect: "none" }}>
                  {t("Ver qué contestó HealthSherpa", "See what HealthSherpa replied")}
                </summary>
                <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 8, background: "var(--c-surface2,#0b1c2b)", border: "1px solid var(--c-border,#1c3a4f)" }}>
                  <pre style={{ margin: 0, fontSize: 11.5, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-all", fontFamily: "var(--font-mono,monospace)", color: "var(--c-text2,#7aa3c0)" }}>
{`GET ${diag.url}
HTTP ${diag.status}

${diag.body || t("(sin cuerpo)", "(no body)")}`}
                  </pre>
                  <button
                    onClick={() => {
                      const texto = `GET ${diag.url}\nHTTP ${diag.status}\n\n${diag.body || ""}`;
                      try {
                        navigator.clipboard.writeText(texto);
                        setCopiado(true);
                        setTimeout(() => setCopiado(false), 2000);
                      } catch { /* sin portapapeles: el texto se ve igual arriba */ }
                    }}
                    style={{ ...btn("transparent", "#00d4ff"), marginTop: 10, padding: "6px 10px", fontSize: 12.5 }}
                  >
                    {copiado ? <Check size={13} /> : <Copy size={13} />}
                    {copiado ? t("Copiado", "Copied") : t("Copiar para soporte", "Copy for support")}
                  </button>
                  <div style={{ ...help, marginTop: 8 }}>
                    {t(
                      "Esto es lo que respondió el servidor. Si el mensaje es de HealthSherpa, falta su aprobación. Si habla de «proxy», «host» o «egress», el problema es nuestro — mándanoslo.",
                      "This is what the server replied. If the message is from HealthSherpa, their approval is missing. If it mentions «proxy», «host» or «egress», the problem is on our side — send it to us.",
                    )}
                  </div>
                </div>
              </details>
            )}
            {state === "pending_approval" && (
              <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, background: "rgba(251,191,36,.08)", border: "1px solid rgba(251,191,36,.3)", fontSize: 13, lineHeight: 1.55 }}>
                {t(
                  "Guardamos tu llave y HealthSherpa la reconoce, pero todavía no te ha dado acceso al estado de pólizas. Eso lo autorizan ellos y no se arregla desde aquí: pídeselo a tu contacto de HealthSherpa. Cuando te digan que ya está, dale a «Comprobar ahora».",
                  "Your key is saved and HealthSherpa recognises it, but they haven't given you access to policy status yet. Only they can authorise that, and it can't be fixed from here: ask your HealthSherpa contact. Once they say it's done, hit \u00abCheck now\u00bb.",
                )}
              </div>
            )}
            {/* NUESTRO FALLO, NO SUYO. Sin este estado, un par\u00e1metro mal
                puesto por nosotros acababa pintado como "se perdi\u00f3 la
                conexi\u00f3n", cuya pantalla dice "pega una llave nueva" \u2014 y el
                agente rotaba una llave perfectamente buena para nada. */}
            {state === "request_invalid" && (
              <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, background: "rgba(122,163,192,.1)", border: "1px solid var(--c-border,#1c3a4f)", fontSize: 13, lineHeight: 1.55 }}>
                {t(
                  "Tu cuenta est\u00e1 bien conectada: HealthSherpa nos deja entrar con tu llave. Lo que falla es nuestra petici\u00f3n \u2014 les estamos pidiendo los datos de una forma que no aceptan. Es cosa nuestra y lo estamos corrigiendo. NO generes otra llave: la tuya est\u00e1 bien.",
                  "Your account is properly connected: HealthSherpa lets us in with your key. What fails is our request \u2014 we're asking for the data in a way they don't accept. That's on us and we're fixing it. Do NOT generate another key: yours is fine.",
                )}
              </div>
            )}
            {state === "disconnected" && (
              <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.3)", fontSize: 13, lineHeight: 1.55 }}>
                {t(
                  "Deskte ya no puede entrar a tu HealthSherpa. Suele ser porque generaste una llave nueva allá: si es así, pégala aquí abajo. Tus clientes y pólizas siguen intactos.",
                  "Deskte can no longer reach your HealthSherpa. Usually that means you generated a new key there — if so, paste it below. Your clients and policies are untouched.",
                )}
              </div>
            )}
            {state === "connected" && (
              <div style={{ ...help, marginTop: 12 }}>
                {t("Funcionando. No hay nada que hacer.", "Working. Nothing to do.")}
              </div>
            )}
          </div>

          {/* Los pasos + el formulario */}
          {(openForm || !conn?.connected) ? (
            <div style={card}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>
                {conn?.connected
                  ? t("Pegar una llave nueva", "Paste a new key")
                  : t("Conectar tu cuenta", "Connect your account")}
              </div>

              <ol style={{ margin: "0 0 18px", paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: "var(--c-text2,#7aa3c0)" }}>
                <li>
                  {t("Crea tu cuenta de desarrollador en HealthSherpa y enlaza ahí tu cuenta de agente.", "Create your developer account at HealthSherpa and link your agent account there.")}{" "}
                  <a href={PORTAL_URL} target="_blank" rel="noreferrer" style={{ color: "#00d4ff", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {t("Abrir HealthSherpa", "Open HealthSherpa")} <ExternalLink size={12} />
                  </a>
                </li>
                <li>{t("Pide el acceso de “on-exchange enrollment”. El estado de pólizas viene incluido, no se pide aparte.", "Request “on-exchange enrollment” access. Policy status is included — you don't request it separately.")}</li>
                <li>{t("Genera tu llave. HealthSherpa la enseña UNA sola vez: cópiala antes de cerrar.", "Generate your key. HealthSherpa shows it ONCE: copy it before closing.")}</li>
                <li>{t("Pégala aquí abajo.", "Paste it below.")}</li>
              </ol>

              <div style={{ display: "grid", gap: 14 }}>
                <div>
                  <label style={lbl}>{t("Correo de tu cuenta", "Your account email")}</label>
                  <input style={inp} type="email" autoComplete="off" placeholder="tucorreo@ejemplo.com"
                    value={form.accountEmail}
                    onChange={(e) => setForm((f) => ({ ...f, accountEmail: e.target.value }))} />
                  <div style={help}>
                    {t(
                      "Tiene que ser EL MISMO de tu cuenta de agente de HealthSherpa. Ellos lo piden así, y con un correo distinto el trámite de aprobación se complica.",
                      "It has to be THE SAME as your HealthSherpa agent account. They ask for it that way, and a different email makes the approval slower.",
                    )}
                  </div>
                </div>

                <div>
                  <label style={lbl}>{t("Tu llave de HealthSherpa", "Your HealthSherpa key")}</label>
                  <input style={inp} type="password" autoComplete="off" placeholder="••••••••••••••••"
                    value={form.apiKey}
                    onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))} />
                  <div style={help}>
                    {t(
                      "Se guarda cifrada (AES-256) y no se vuelve a mostrar, ni a ti ni a nadie.",
                      "Stored encrypted (AES-256) and never shown again, to you or anyone.",
                    )}
                  </div>
                </div>

                {/* Lo pidió HealthSherpa explícitamente: el agente transacciona
                    con esas APIs y tiene que estar al tanto de sus términos. Se
                    guarda la fecha en que lo marcó — no se puede retro-anotar. */}
                <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 13, lineHeight: 1.5, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.acceptedTerms} style={{ marginTop: 3 }}
                    onChange={(e) => setForm((f) => ({ ...f, acceptedTerms: e.target.checked }))} />
                  <span>
                    {t("Conozco los ", "I've seen HealthSherpa's ")}
                    <a href={TERMS_URL} target="_blank" rel="noreferrer" style={{ color: "#00d4ff", textDecoration: "none" }}>
                      {t("términos de desarrollador de HealthSherpa", "developer terms")}
                    </a>
                    {t(
                      ". La cuenta y la llave son mías, y soy yo quien usa esas APIs.",
                      ". The account and the key are mine, and I'm the one using those APIs.",
                    )}
                  </span>
                </label>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={handleSave} disabled={busy} style={btn("#16a34a")}>
                    {busy ? <Loader2 size={16} className="dkt-spin" /> : <Check size={16} />}
                    {t("Guardar", "Save")}
                  </button>
                  {conn?.connected && (
                    <button onClick={() => { setOpenForm(false); setError(""); }} disabled={busy} style={btn("transparent", "#7aa3c0")}>
                      {t("Cancelar", "Cancel")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={card}>
              <button onClick={() => { setOpenForm(true); setError(""); }} style={btn("transparent", "#00d4ff")}>
                <Plug size={15} /> {t("Pegar una llave nueva", "Paste a new key")}
              </button>
              <div style={{ ...help, marginTop: 8 }}>
                {t(
                  "Solo si generaste otra llave en HealthSherpa. La que está guardada se reemplaza.",
                  "Only if you generated another key at HealthSherpa. The stored one gets replaced.",
                )}
              </div>
            </div>
          )}

          {/* EL VISTAZO. Solo con la conexión en verde, porque antes no hay
              nada que mirar.

              QUE HACE Y QUE NO: pregunta a HealthSherpa y enseña lo que
              contestan. NO guarda nada — ni un cliente, ni una póliza, ni una
              fila. Al recargar la pantalla desaparece.

              POR QUE EXISTE: hasta que la llave quedó autorizada (2026-10-26)
              nunca habíamos visto una respuesta buena suya. Todo lo que viene
              después — traducir sus campos a una póliza de Deskte, emparejar
              con el cliente correcto — depende de cómo se llamen esos campos.
              Hacerlo a ciegas es como se pierden datos en silencio: un campo
              que no se recoge no da error, simplemente no se guarda. */}
          {state === "connected" && (
            <div style={card}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
                {t("Ver lo que manda HealthSherpa", "See what HealthSherpa sends")}
              </div>
              <div style={{ ...help, marginTop: 0, marginBottom: 14 }}>
                {t(
                  "Solo mira. No guarda nada ni toca a tus clientes. Sirve para comprobar que los datos que mandan son los que esperamos antes de dejar que escriban en Deskte.",
                  "It only looks. It saves nothing and touches none of your clients. It's to check that what they send is what we expect before letting it write into Deskte.",
                )}
              </div>

              <button onClick={handleVistazo} disabled={mirando} style={btn("transparent", "#00d4ff")}>
                {mirando ? <Loader2 size={15} className="dkt-spin" /> : <Eye size={15} />}
                {mirando ? t("Mirando…", "Looking…") : t("Mirar ahora", "Look now")}
              </button>

              {/* HealthSherpa dijo que no. El cuerpo va AQUI MISMO y abierto:
                  es lo unico que dice que les falta, y en la primera version
                  estaba escondido en un desplegable de otra tarjeta. */}
              {diagVistazo && (
                <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, background: "var(--c-surface2,#0b1c2b)", border: "1px solid rgba(248,113,113,.3)" }}>
                  <div style={{ fontSize: 12.5, marginBottom: 8, color: "#fca5a5" }}>
                    {t("Esto es lo que contestó HealthSherpa:", "This is what HealthSherpa replied:")}
                  </div>
                  <pre style={{ margin: 0, fontSize: 11.5, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono,monospace)", color: "var(--c-text2,#7aa3c0)", maxHeight: 260, overflow: "auto" }}>
{`GET ${diagVistazo.url}
HTTP ${diagVistazo.status ?? "—"}

${diagVistazo.body || diagVistazo.networkError || t("(sin cuerpo)", "(no body)")}`}
                  </pre>
                  <button
                    onClick={() => {
                      const texto = `GET ${diagVistazo.url}\nHTTP ${diagVistazo.status ?? "—"}\n\n${diagVistazo.body || diagVistazo.networkError || ""}`;
                      try {
                        navigator.clipboard.writeText(texto);
                        setCopiadoResumen(true);
                        setTimeout(() => setCopiadoResumen(false), 2000);
                      } catch { /* sin portapapeles: el texto se ve igual arriba */ }
                    }}
                    style={{ ...btn("transparent", "#00d4ff"), marginTop: 10, padding: "6px 10px", fontSize: 12.5 }}
                  >
                    {copiadoResumen ? <Check size={13} /> : <Copy size={13} />}
                    {copiadoResumen ? t("Copiado", "Copied") : t("Copiar para soporte", "Copy for support")}
                  </button>
                  <div style={{ ...help, marginTop: 8 }}>
                    {t(
                      "Un fallo aquí no afecta a tu conexión ni a tus clientes: esto solo mira. Mándanos este texto y se corrige.",
                      "A failure here doesn't affect your connection or your clients: this only looks. Send us this text and we'll fix it.",
                    )}
                  </div>
                </div>
              )}

              {vistazo && (
                <div style={{ marginTop: 16 }}>
                  {/* EL CONTADOR CUENTA LA TABLA, no el resumen. Desde que
                      se preguntan DOS años en temporada (E-389), `resumen`
                      describe la forma de la respuesta del PRIMER año que
                      contestó, así que `resumen.filas` eran solo las de ese
                      año: Lissette vio "Mandaron 21" encima de una tabla con
                      23 filas, y las dos que faltaban eran justo las suyas
                      del 2027, las que de verdad quería ver. */}
                  <div style={{ fontSize: 13, marginBottom: 10 }}>
                    {vistazo.items.length === 0
                      ? t(
                          "HealthSherpa contestó bien, pero no mandó ninguna solicitud. Puede ser que no haya nada en estos años de plan, o que haya que pedirles otro filtro.",
                          "HealthSherpa answered fine, but sent no applications. Either there's nothing for these plan years, or we need to ask them with a different filter.",
                        )
                      : t(
                          `Mandaron ${vistazo.items.length} solicitud(es).`,
                          `They sent ${vistazo.items.length} application(s).`,
                        )}
                    {vistazo.anios?.length > 1 && (
                      <span style={{ color: "var(--c-text2,#7aa3c0)" }}>
                        {" "}{t(`Años consultados: ${vistazo.anios.join(" y ")}.`,
                              `Plan years checked: ${vistazo.anios.join(" and ")}.`)}
                      </span>
                    )}
                    {vistazo.aniosConFallo?.length > 0 && (
                      <span style={{ color: "#fbbf24" }}>
                        {" "}{t(`No contestó el ${vistazo.aniosConFallo.join(" ni el ")}.`,
                              `No answer for ${vistazo.aniosConFallo.join(" or ")}.`)}
                      </span>
                    )}
                  </div>

                  {vistazo.items.length > 0 && <TablaVistazo items={vistazo.items} t={t} />}

                  {/* EL ESTADO DE VERDAD. La tabla de arriba son solo
                      identificadores: si una póliza está activa o cancelada,
                      hasta cuándo está pagada y cuánto debe, vive en una
                      segunda consulta por póliza. Se leen unas pocas porque
                      cada una gasta cuota de la agente. */}
                  {vistazo.detalles?.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                        {t(
                          `Estado a fondo de ${vistazo.detalles.length} de ellas`,
                          `Full status for ${vistazo.detalles.length} of them`,
                        )}
                      </div>
                      <TablaVistazo items={vistazo.detalles.map((d) => d.datos)} t={t} />
                    </div>
                  )}

                  {/* En alpha, que falte el estado de una póliza es NORMAL y
                      lo dicen ellos. Decirlo aquí evita que parezca un fallo
                      de Deskte. */}
                  {vistazo.detallesFallidos?.length > 0 && (
                    <div style={{ ...help, marginTop: 10 }}>
                      {t(
                        `${vistazo.detallesFallidos.length} no devolvieron estado todavía. HealthSherpa avisa de que su servicio de estado está en pruebas y que aún no todas las pólizas lo traen — no es un fallo tuyo ni nuestro.`,
                        `${vistazo.detallesFallidos.length} didn't return a status yet. HealthSherpa warns their status service is in alpha and not every policy carries one yet — it's not your fault or ours.`,
                      )}
                    </div>
                  )}

                  {vistazo.frenados && (
                    <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(251,191,36,.08)", border: "1px solid rgba(251,191,36,.3)", fontSize: 12.5, lineHeight: 1.55 }}>
                      {t(
                        "HealthSherpa nos frenó por número de consultas, así que paramos ahí en vez de insistir. Vuelve a darle en un rato.",
                        "HealthSherpa rate-limited us, so we stopped there instead of pushing. Try again in a little while.",
                      )}
                    </div>
                  )}

                  {/* EL RESUMEN DE CAMPOS. Esto es lo único de aquí pensado
                      para SALIR de la pantalla: lleva los nombres de los
                      campos y sus tipos, pero no los valores que puedan ser
                      datos personales — el servidor los tapa antes de
                      mandarlos (utils/healthSherpaForma.ts). */}
                  <details style={{ marginTop: 14 }} open={vistazo.items.length === 0}>
                    <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", userSelect: "none" }}>
                      {t("Qué campos mandan (para soporte)", "Which fields they send (for support)")}
                    </summary>
                    <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 8, background: "var(--c-surface2,#0b1c2b)", border: "1px solid var(--c-border,#1c3a4f)" }}>
                      <pre style={{ margin: 0, fontSize: 11.5, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono,monospace)", color: "var(--c-text2,#7aa3c0)", maxHeight: 320, overflow: "auto" }}>
                        {vistazo.resumenDetalleTexto
                          // El resumen del detalle va DEBAJO del de la lista,
                          // no en su lugar: el traductor necesita los dos, y
                          // la lista es la que dice dónde está la paginación.
                          ? `${vistazo.resumenTexto}\n\n${"-".repeat(40)}\nDETALLE (segunda consulta)\n\n${vistazo.resumenDetalleTexto}`
                          : vistazo.resumenTexto}
                      </pre>
                      <button
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText(
                              vistazo.resumenDetalleTexto
                                ? `${vistazo.resumenTexto}\n\n${"-".repeat(40)}\nDETALLE (segunda consulta)\n\n${vistazo.resumenDetalleTexto}`
                                : vistazo.resumenTexto,
                            );
                            setCopiadoResumen(true);
                            setTimeout(() => setCopiadoResumen(false), 2000);
                          } catch { /* sin portapapeles: el texto se ve igual arriba */ }
                        }}
                        style={{ ...btn("transparent", "#00d4ff"), marginTop: 10, padding: "6px 10px", fontSize: 12.5 }}
                      >
                        {copiadoResumen ? <Check size={13} /> : <Copy size={13} />}
                        {copiadoResumen ? t("Copiado", "Copied") : t("Copiar resumen", "Copy summary")}
                      </button>
                      <div style={{ ...help, marginTop: 8 }}>
                        {t(
                          "Este resumen se puede pegar en un chat sin problema: lleva los NOMBRES de los campos, no los datos de tus clientes. Los que podrían llevarlos salen como «(ocultos)».",
                          "This summary is safe to paste into a chat: it carries field NAMES, not your clients' data. Any field that could carry it shows as «(ocultos)».",
                        )}
                      </div>
                    </div>
                  </details>
                </div>
              )}
            </div>
          )}

          {/* Lo que todavía NO hace. Decirlo aquí evita la pregunta de por qué
              está conectado y no se ve nada nuevo. */}
          <div style={{ ...card, background: "transparent", borderStyle: "dashed" }}>
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
              <ShieldCheck size={16} color="#7aa3c0" style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.6 }}>
                {t(
                  "Por ahora Deskte solo MIRA lo que hay en HealthSherpa: todavía no actualiza ninguna póliza por su cuenta. Ese es el siguiente paso, y hasta que esté, el estado de tus pólizas se sigue cambiando a mano. Los documentos pendientes (DMI) no viajan por esta vía: HealthSherpa no los manda.",
                  "For now Deskte only LOOKS at what's in HealthSherpa: it doesn't update any policy on its own yet. That's the next step, and until it's done, policy status is still changed by hand. Pending documents (DMI) don't travel this way — HealthSherpa doesn't send them.",
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** La tabla del vistazo.
 *
 *  NO SABE QUE CAMPOS VAN A VENIR, y es a propósito: esta tabla existe
 *  justamente para descubrirlos. Así que se arma con las claves que traigan
 *  las filas, sean las que sean. El día que se sepa la forma de verdad, esto
 *  se cambia por una tabla con columnas de verdad (fecha de efecto, estado,
 *  prima) — y entonces este componente sobra.
 *
 *  Solo se pintan las columnas de valor simple. Lo anidado se ve entero en el
 *  desplegable de cada fila, que es donde hay que mirar si falta algo. */
function TablaVistazo({ items, t }) {
  // La unión de claves de TODAS las filas, no solo la primera: un campo que
  // solo aparece cuando la póliza está cancelada se perdería mirando una sola.
  const columnas = [];
  for (const fila of items) {
    if (!fila || typeof fila !== "object") continue;
    for (const [k, v] of Object.entries(fila)) {
      if (v !== null && typeof v === "object") continue;
      if (!columnas.includes(k)) columnas.push(k);
    }
  }
  const visibles = columnas.slice(0, 8);

  const celda = {
    padding: "7px 10px", fontSize: 12, textAlign: "left",
    borderBottom: "1px solid var(--c-border,#1c3a4f)", whiteSpace: "nowrap",
  };

  return (
    <div>
      <div style={{ overflowX: "auto", border: "1px solid var(--c-border,#1c3a4f)", borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {visibles.map((k) => (
                <th key={k} style={{ ...celda, color: "var(--c-text2,#7aa3c0)", fontWeight: 700 }}>{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((fila, i) => (
              <tr key={i}>
                {visibles.map((k) => (
                  <td key={k} style={celda}>
                    {fila && typeof fila === "object" && fila[k] !== null && fila[k] !== undefined
                      ? String(fila[k])
                      : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {columnas.length > visibles.length && (
        <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 6 }}>
          {t(
            `Se enseñan ${visibles.length} de ${columnas.length} columnas. El resto está en el desplegable de cada fila.`,
            `Showing ${visibles.length} of ${columnas.length} columns. The rest is in each row's expander.`,
          )}
        </div>
      )}
      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", userSelect: "none" }}>
          {t("Ver una fila entera, tal cual la mandan", "See a whole row, exactly as they send it")}
        </summary>
        <pre style={{ marginTop: 10, padding: "12px 14px", borderRadius: 8, background: "var(--c-surface2,#0b1c2b)", border: "1px solid var(--c-border,#1c3a4f)", fontSize: 11.5, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono,monospace)", color: "var(--c-text2,#7aa3c0)", maxHeight: 360, overflow: "auto" }}>
          {JSON.stringify(items[0], null, 2)}
        </pre>
        <div style={{ fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 6 }}>
          {t(
            "Esto son datos de tus clientes: míralo tú, no lo pegues en ningún chat. Para eso está el resumen de campos de abajo.",
            "This is your clients' data: look at it yourself, don't paste it into any chat. That's what the field summary below is for.",
          )}
        </div>
      </details>
    </div>
  );
}
